const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/v1';
import { apiFetch, handleApiResponse } from './interceptor';
import Swal from 'sweetalert2';

// Add OTP verification and resend methods
const verifyOtp = async ({ userId, mobileNumber, otp }) => {
  const response = await fetch(`${BASE_URL}/auth/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mobileNumber, otp }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'OTP verification failed');
  }
  return response.json();
};

const resendOtp = async ({ userId, mobileNumber }) => {
  const response = await fetch(`${BASE_URL}/auth/resend-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mobileNumber }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to resend OTP');
  }
  return response.json();
};

export const authApi = {
  login: async (credentials) => {
    try {
      const response = await apiFetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
        toast: true
      }, false);
      
      // Check if we got a redirected response
      if (response.redirected) {
        return response; // No need to process further
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }
      
      const data = await response.json();
      console.log('Login response:', data);
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  register: async (userData) => {
    try {
      // Enhanced validation with field-specific errors
      const errors = {};
      
      // Validate name
      if (!userData.name || !userData.name.trim()) {
        errors.field = 'name';
        errors.message = 'Full Name is required';
        throw errors;
      }
      
      // Validate mobile number
      if (!userData.mobileNumber) {
        errors.field = 'mobileNumber';
        errors.message = 'Mobile Number is required';
        throw errors;
      }
      
      // Validate mobile number format
      const cc = userData.mobileNumber.match(/^\+?\d{1,3}/)?.[0] || '';
      const number = userData.mobileNumber.replace(/^\+?\d{1,3}/, '');
      if (cc === '+63' || cc === '63') {
        if (!/^\d{10}$/.test(number)) {
          errors.field = 'mobileNumber';
          errors.message = 'PH mobile number must be 10 digits (e.g., +639123456789)';
          throw errors;
        }
        if (number.startsWith('0')) {
          errors.field = 'mobileNumber';
          errors.message = 'Do not include leading zero for PH numbers';
          throw errors;
        }
      } else if (cc === '+1' || cc === '1' || cc === '+44' || cc === '44' || cc === '+91' || cc === '91') {
        if (!/^\d{10}$/.test(number)) {
          errors.field = 'mobileNumber';
          errors.message = 'Mobile number must be 10 digits (country code: ' + cc + ')';
          throw errors;
        }
      } else {
        if (!/^\d{8,12}$/.test(number)) {
          errors.field = 'mobileNumber';
          errors.message = 'Mobile number must be 8-12 digits (excluding country code)';
          throw errors;
        }
      }
      
      // Validate password
      if (!userData.password) {
        errors.field = 'password';
        errors.message = 'Password is required';
        throw errors;
      }
      
      // Validate password strength
      if (userData.password.length < 8) {
        errors.field = 'password';
        errors.message = 'Password must be at least 8 characters';
        throw errors;
      }
      
      // Validate referral code
      // if (!userData.referralCode || !userData.referralCode.trim()) {
      //   errors.field = 'referralCode';
      //   errors.message = 'Referral Code is required';
      //   throw errors;
      // }

      // Make API request using apiFetch for consistency with other endpoints
      const response = await apiFetch(`${BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
        toast: false // Disable default toast to handle custom errors
      }, false).catch(err => {
        console.error('Fetch error:', err);
        throw new Error(err.message);
      });

      if (response.ok === false) {
        if (typeof response.json === 'function') {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Registration failed');
        } else {
          // This is a logout/redirect object, not a real response
          throw new Error('Session expired or unauthorized. Please log in again.');
        }
      }
      
      // Check if we got a redirected response
      if (response.redirected) {
        return response; // No need to process further
      }
      

      // Handle successful registration
      if (response.status === 201 || response.ok) {
        const data = await response.json();
        console.log('Registration successful:', data);
        
        // Don't show success message here, let the component handle it
        // This prevents duplicate notifications
        return data;
      }
      
      // Handle API error responses
      const errorData = await response.json();
      
      // Check if the API returned field-specific errors
      if (errorData.field) {
        throw {
          field: errorData.field,
          message: errorData.message || 'Invalid input'
        };
      }
      
      // Handle general errors
      throw new Error(errorData.message || 'Registration failed');
      
    } catch (error) {
      console.error('Registration error:', error);
      
      // Re-throw field-specific errors to the component
      if (error.field) {
        throw error;
      }
      
      // Handle generic errors
      throw new Error(error.message || 'An unexpected error occurred');
    }
  },

  forgotPassword: async (phoneNumber) => {
    try {
      const response = await apiFetch(`${BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber }),
      });
      
      // Check if we got a redirected response
      if (response.redirected) {
        return response; // No need to process further
      }
      
      return await response.json();
    } catch (error) {
      throw new Error('Password reset request failed: ' + error.message);
    }
  },

  verifyOtp,
  resendOtp,

  // Add other auth-related endpoints as needed
}; 